<template>
  <div
    v-if="shouldShow"
    class="flex items-center justify-between gap-2 py-4 border-b border-divider w-full transition-colors duration-500 ease-sidebar motion-reduce:transition-none group-[&.collapsed]/wrapper:border-transparent group-[&.collapsed]/wrapper:gap-0"
  >
  <div
    data-testid="org-switcher-collapsed-logo"
    class="hidden justify-center w-full group-[&.collapsed]/wrapper:flex"
    :title="currentOrg?.name"
  >
    <OrgAvatar :org="currentOrg" shape="rounded-lg" size="w-6 h-6" />
  </div>
  <div
    class="w-full content-transition group-[&.collapsed]/wrapper:opacity-0 group-[&.collapsed]/wrapper:invisible group-[&.collapsed]/wrapper:h-0 group-[&.collapsed]/wrapper:w-0 group-[&.collapsed]/wrapper:overflow-hidden"
  >
    <button
      @click="toggleOpen"
      :class="[
        'flex items-center w-full gap-2 px-2 py-2 rounded-lg transition-colors',
        isOpen ? 'bg-disabled/15' : 'hover:bg-disabled/10',
      ]"
    >
      <OrgAvatar :org="currentOrg" shape="rounded-lg" size="w-7 h-7" />
      <span class="flex-1 text-left text-xs font-semibold text-theme-primary truncate">
        {{ currentOrg?.name }}
      </span>
      <svg
        :class="isOpen ? '-rotate-180' : 'rotate-0'"
        class="w-3.5 h-3.5 shrink-0 text-theme-disabled transition-transform duration-200"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <div v-if="isOpen">
      <div v-if="otherOrgs.length > 5" class="px-2 py-1.5">
        <input
          v-model="searchTerm"
          type="text"
          :placeholder="$t('Search')"
          class="w-full px-2 py-1 text-xs bg-transparent border-b border-grey-200 dark:border-theme-divider focus:outline-none focus:border-theme-primary text-theme-primary placeholder:text-theme-disabled"
        />
      </div>

      <div class="max-h-48 overflow-y-auto org-scroll">
      <button
        v-for="org in filteredOrgs"
        :key="org.id"
        @click="switchOrg(org.id)"
        class="flex items-center w-full gap-2 px-2 py-1.5 rounded-lg hover:bg-disabled/10 transition-colors"
      >
        <OrgAvatar :org="org" shape="rounded-full" size="w-7 h-7" />
        <span class="text-xs font-medium text-theme-secondary truncate">{{ org.name }}</span>
      </button>
      <p v-if="filteredOrgs.length === 0" class="px-2 py-1.5 text-xs text-theme-disabled">
        {{ $t('No results') }}
      </p>
      </div>

      <Link
        :href="route('admin')"
        class="block px-2 py-1.5 text-xs text-theme-disabled hover:text-theme-secondary transition-colors"
      >
        {{ $t('Admin Settings') }}
      </Link>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, computed, defineComponent, h } from 'vue';
import { Link, usePage, router } from '@inertiajs/vue3';

const OrgAvatar = defineComponent({
  props: {
    org: { type: Object, default: null },
    shape: { type: String, default: 'rounded-lg' },
    size: { type: String, default: 'w-7 h-7' },
  },
  setup(props) {
    const DEFAULT_LOGO = '/images/ApplicationLogo.svg';

    const hasRealAvatar = computed(
      () => props.org?.avatar_image_url?.url && props.org.avatar_image_url.url !== DEFAULT_LOGO,
    );

    const initial = computed(() => props.org?.name?.charAt(0)?.toUpperCase() ?? '?');

    return () =>
      hasRealAvatar.value
        ? h('img', {
            src: props.org.avatar_image_url.url,
            alt: props.org?.name ?? '',
            class: `${props.size} ${props.shape} object-cover shrink-0 border border-grey-200 dark:border-theme-divider`,
          })
        : h(
            'div',
            {
              class: `${props.size} ${props.shape} shrink-0 flex items-center justify-center bg-grey-100 dark:bg-grey-700 text-theme-secondary text-[10px] font-bold border border-grey-200 dark:border-theme-divider`,
            },
            initial.value,
          );
  },
});

const page = usePage();

const currentOrg = computed(() => page.props.auth?.organization ?? null);
const userOrgs = computed(() => page.props.userOrganizations ?? []);
const isSuperUser = computed(() => page.props.isSuperUser ?? false);

const shouldShow = computed(() => isSuperUser.value || userOrgs.value.length > 1);

const otherOrgs = computed(() =>
  userOrgs.value.filter((org) => org.id !== currentOrg.value?.id),
);

const isOpen = ref(false);
const searchTerm = ref('');

const filteredOrgs = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) {
    return otherOrgs.value;
  }
  return otherOrgs.value.filter((org) => org.name?.toLowerCase().includes(term));
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) {
    searchTerm.value = '';
  }
};

const switchOrg = (orgId) => {
  isOpen.value = false;
  searchTerm.value = '';
  router.visit(route('switch.company', { organization_id: orgId }));
};
</script>

<style scoped>
.org-scroll::-webkit-scrollbar       { width: 4px; }
.org-scroll::-webkit-scrollbar-track { background: transparent; }
.org-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 9999px; }
.org-scroll:hover::-webkit-scrollbar-thumb { background: var(--color-gray-300); } /* grey-300 */
</style>
